import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import PriceIntelligenceSampleTable, {
  CampoOrdenacao,
  SortMode,
} from 'components/molecules/price_intelligence_sample_table';
import PriceIntelligenceBiddingTable from 'components/molecules/price_intelligence_bidding_table';
import PriceIntelligenceRefine from 'components/molecules/price_intelligence_refine';
import PriceIntelligenceOrgaoModal from 'components/molecules/price_intelligence_orgao_modal';
import ProvidersModal from 'components/atoms/providers_modal';
import {
  ChartCard,
  Histogram,
  RegionChart,
  TrendChart,
  VolumeChart,
} from 'components/molecules/price_intelligence_charts';
import createTrack from 'services/actions/tracker_actions';
import { getCompanyData, getProviderBiddings } from 'store/modules/market_store/actions';
import {
  agruparPorLicitacao,
  CompradorAgregado,
  exportarCsv,
  FILTROS_AMOSTRAS_VAZIOS,
  filtrarAmostras,
  FiltrosAmostras,
  fmtBRL,
  fmtBRLCompact,
  fmtDataBR,
  fmtInt,
  referenciaDaCesta,
  referenciaDoResultado,
  SavedSearchSnapshot,
  SearchHit,
  SearchResponse,
  serieMensal,
  snapshotDoResultado,
  topFornecedores,
  topOrgaos,
  totalFiltrosAmostras,
  unidadeCanonica,
  unidadeLabel,
  valorComparavelDe,
  volumeMensal,
} from 'services/price_intelligence';
import { Container } from './styles';

type Visao = 'amostras' | 'licitacoes';
const TOP = 5;

export interface QuantidadeProjeto {
  valor: number;
  unidade: string | null;
}

interface PriceIntelligenceResultsProps {
  resultado: SearchResponse;
  onSalvar: (snapshot: SavedSearchSnapshot) => void;
  periodoMeses: number;
  onAmpliarPeriodo: () => void;
  projetoQtd: QuantidadeProjeto | null;
  podeRefinar: boolean;
  onRefinar: () => void;
  regiaoDestaque: string | null;
  loteRodando: boolean;
  onFiltros: (filtros: FiltrosAmostras) => void;
}

const PriceIntelligenceResults = ({
  resultado,
  onSalvar,
  periodoMeses,
  onAmpliarPeriodo,
  projetoQtd,
  podeRefinar,
  onRefinar,
  regiaoDestaque,
  loteRodando,
  onFiltros,
}: PriceIntelligenceResultsProps) => {
  const dispatch = useDispatch();
  const impressaoRef = useRef<HTMLDivElement>(null);
  const [selecionados, setSelecionados] = useState<Set<string | number>>(new Set());
  const [sortMode, setSortMode] = useState<SortMode>('relevancia');
  const [visiveis, setVisiveis] = useState<number>(25);
  const [filtros, setFiltros] = useState<FiltrosAmostras>(FILTROS_AMOSTRAS_VAZIOS);
  const [visao, setVisao] = useState<Visao>('amostras');
  const [todosFornecedores, setTodosFornecedores] = useState<boolean>(false);
  const [todosOrgaos, setTodosOrgaos] = useState<boolean>(false);
  const [orgaoAberto, setOrgaoAberto] = useState<CompradorAgregado | null>(null);
  const [fichaCnpj, setFichaCnpj] = useState<string | null>(null);
  const [fichaAberta, setFichaAberta] = useState<boolean>(false);
  const [paginaFicha, setPaginaFicha] = useState<number>(1);

  const { hits: todasAmostras, stats, curation, alerts } = resultado;
  const filtrando = totalFiltrosAmostras(filtros) > 0;
  const hits = useMemo(() => filtrarAmostras(todasAmostras, filtros), [todasAmostras, filtros]);
  const regime = resultado.regime || 'produto';
  const unidadeRef = regime === 'unitario' ? resultado.unidade_referencia || null : null;
  const uniLabel = unidadeLabel(unidadeRef);
  const sufixoUnidade = uniLabel ? `/${uniLabel}` : '';
  const rotuloUnidade = uniLabel ? ` (R$/${uniLabel})` : '';
  const grupoUnidade =
    unidadeRef && resultado.unit_stats ? resultado.unit_stats[unidadeRef] || null : null;
  const grupoGlobal = resultado.unit_stats ? resultado.unit_stats.GLOBAL || null : null;

  const valorComparavel = useCallback(
    (h: SearchHit) => valorComparavelDe(resultado, h),
    [resultado],
  );

  const hitsOrdenados = useMemo(() => {
    const lista = hits.slice();
    const valorDe = (h: SearchHit) => valorComparavel(h) || 0;
    const dataDe = (h: SearchHit) => h.payload.created || '';
    if (sortMode === 'valor_asc') lista.sort((a, b) => valorDe(a) - valorDe(b));
    if (sortMode === 'valor_desc') lista.sort((a, b) => valorDe(b) - valorDe(a));
    if (sortMode === 'data_desc') lista.sort((a, b) => dataDe(b).localeCompare(dataDe(a)));
    if (sortMode === 'data_asc') lista.sort((a, b) => dataDe(a).localeCompare(dataDe(b)));
    return lista;
  }, [hits, sortMode, valorComparavel]);

  const hitsVisiveis = hitsOrdenados.slice(0, visiveis);

  const cesta = useMemo(() => {
    if (!selecionados.size) return hits;
    return hits.filter((h) => selecionados.has(h.id));
  }, [hits, selecionados]);

  const referencia = useMemo(
    () =>
      selecionados.size || filtrando
        ? referenciaDaCesta(cesta, valorComparavel)
        : referenciaDoResultado(resultado),
    [cesta, resultado, selecionados, filtrando, valorComparavel],
  );

  const valoresGrafico = useMemo(
    () => hits.map(valorComparavel).filter((v): v is number => typeof v === 'number' && v > 0),
    [hits, valorComparavel],
  );

  const serie = useMemo(
    () =>
      regime === 'produto' && !filtrando
        ? resultado.timeseries
        : serieMensal(hits, valorComparavel),
    [hits, regime, filtrando, resultado.timeseries, valorComparavel],
  );

  const volume = useMemo(() => volumeMensal(hits), [hits]);

  const fornecedores = useMemo(
    () => topFornecedores(hits, valorComparavel, Infinity),
    [hits, valorComparavel],
  );
  const orgaos = useMemo(() => topOrgaos(hits, valorComparavel, Infinity), [hits, valorComparavel]);
  const fornecedoresTop = todosFornecedores ? fornecedores : fornecedores.slice(0, TOP);
  const orgaosTop = todosOrgaos ? orgaos : orgaos.slice(0, TOP);
  const licitacoes = useMemo(() => agruparPorLicitacao(hitsOrdenados), [hitsOrdenados]);

  useEffect(() => {
    if (fichaCnpj) dispatch(getProviderBiddings(fichaCnpj, paginaFicha));
  }, [dispatch, fichaCnpj, paginaFicha]);

  const abrirFicha = (cnpj: string) => {
    createTrack('Análise de Preços - Abriu ficha do concorrente', {});
    dispatch(getCompanyData(cnpj));
    setPaginaFicha(1);
    setFichaCnpj(cnpj);
    setFichaAberta(true);
  };

  const abrirOrgao = (orgao: CompradorAgregado) => {
    createTrack('Análise de Preços - Abriu órgão', { com_cadastro: Boolean(orgao.orgaoId) });
    setOrgaoAberto(orgao);
  };

  const mudarVisao = (nova: Visao) => {
    if (nova !== visao)
      createTrack('Análise de Preços - Mudou visão das amostras', { visao: nova });
    setVisao(nova);
  };

  const imprimir = useReactToPrint({
    content: () => impressaoRef.current,
    documentTitle: `analise_mercado_${resultado.query.replace(/[^\w]+/g, '_').slice(0, 40)}`,
    onBeforeGetContent: () => {
      createTrack('Análise de Preços - Imprimiu', { query: resultado.query });
      return Promise.resolve();
    },
  });

  const estimativaProjeto =
    grupoUnidade &&
    typeof grupoUnidade.mediana === 'number' &&
    projetoQtd &&
    (!projetoQtd.unidade || projetoQtd.unidade === unidadeCanonica(unidadeRef))
      ? {
          qtd: projetoQtd.valor,
          total: grupoUnidade.mediana * projetoQtd.valor,
          p25: typeof grupoUnidade.p25 === 'number' ? grupoUnidade.p25 * projetoQtd.valor : null,
          p75: typeof grupoUnidade.p75 === 'number' ? grupoUnidade.p75 * projetoQtd.valor : null,
        }
      : null;

  const indicativo = useMemo(() => {
    if (regime !== 'global' || !resultado.unit_stats) return null;
    const unitStats = resultado.unit_stats;
    const candidatos = Object.keys(unitStats)
      .filter(
        (u) => u !== 'GLOBAL' && unitStats[u].n >= 3 && typeof unitStats[u].mediana === 'number',
      )
      .sort((a, b) => unitStats[b].n - unitStats[a].n);
    if (!candidatos.length) return null;
    return {
      unidade: candidatos[0],
      label: unidadeLabel(candidatos[0]),
      stats: unitStats[candidatos[0]],
    };
  }, [regime, resultado.unit_stats]);

  const toggle = (id: string | number) => {
    setSelecionados((prev) => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  };

  const todosVisiveisSelecionados =
    hitsVisiveis.length > 0 && hitsVisiveis.every((h) => selecionados.has(h.id));

  const toggleTodos = () => {
    setSelecionados((prev) => {
      const novo = new Set(prev);
      if (todosVisiveisSelecionados) {
        hitsVisiveis.forEach((h) => novo.delete(h.id));
      } else {
        hitsVisiveis.forEach((h) => novo.add(h.id));
      }
      return novo;
    });
  };

  const ordenarPor = (campo: CampoOrdenacao) => {
    setSortMode((atual) => {
      if (campo === 'valor') return atual === 'valor_asc' ? 'valor_desc' : 'valor_asc';
      return atual === 'data_desc' ? 'data_asc' : 'data_desc';
    });
  };

  const salvar = () => onSalvar(snapshotDoResultado(resultado, cesta, referencia));

  const baixarCsv = () => {
    createTrack('Análise de Preços - Exportou CSV', {
      query: resultado.query,
      amostras: cesta.length,
    });
    const nome = `analise_mercado_${resultado.query.replace(/[^\w]+/g, '_').slice(0, 40)}`;
    exportarCsv(selecionados.size ? cesta : hitsOrdenados, nome);
  };

  const rotuloCesta = selecionados.size
    ? `da sua seleção de ${fmtInt(cesta.length)} amostras`
    : null;

  const faixa = (p25: number | null, p75: number | null) =>
    p25 !== null && p75 !== null ? `${fmtBRL(p25)} – ${fmtBRL(p75)}` : '—';

  const enrichment = resultado.edital_enrichment;
  const editaisConsultados =
    enrichment && enrichment.ativado ? enrichment.licitacoes_com_edital || 0 : 0;
  const resgatados = enrichment && enrichment.resgate ? enrichment.resgate.aprovados || 0 : 0;
  const metragensDoEdital = enrichment ? enrichment.escopos_derivados_do_edital || 0 : 0;
  const buscas = (resultado.queries_utilizadas || []).filter(
    (q, i, todas) => todas.indexOf(q) === i,
  );

  return (
    <Container ref={impressaoRef}>
      <p className="print-only">
        Análise de Mercado 3.0 · ConLicitação · {fmtDataBR(new Date().toISOString())}
      </p>
      <div className="results-head">
        <div>
          <p className="results-title">Panorama de preços para “{resultado.query}”</p>
          {resultado.spec_assumida && (
            <p className="results-sub">Especificação considerada: {resultado.spec_assumida}</p>
          )}
        </div>
        <div className="results-actions d-print-none">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => imprimir && imprimir()}
          >
            <i className="material-icons">print</i>
            Imprimir
          </button>
        </div>
      </div>

      <PriceIntelligenceRefine
        filtros={filtros}
        onChange={(novos) => {
          setFiltros(novos);
          onFiltros(novos);
        }}
        total={todasAmostras.length}
        filtradas={hits.length}
      />

      {curation && (
        <div className="curation-chip">
          <i className="material-icons">auto_awesome</i>
          <div>
            <span>
              <strong>Curadoria de IA:</strong> {fmtInt(curation.kept)} de{' '}
              {fmtInt(curation.analyzed)} resultados mantidos. {curation.reason}
            </span>
            {editaisConsultados > 0 && (
              <p className="curation-extra">
                <i className="material-icons">description</i>
                Editais consultados: a especificação de {fmtInt(editaisConsultados)}{' '}
                {editaisConsultados === 1 ? 'licitação' : 'licitações'} foi conferida no texto do
                edital
                {metragensDoEdital > 0
                  ? ` — ${fmtInt(metragensDoEdital)} ${
                      metragensDoEdital === 1 ? 'metragem extraída' : 'metragens extraídas'
                    } de lá`
                  : ''}
                {resgatados > 0
                  ? ` — ${fmtInt(resgatados)} ${
                      resgatados === 1
                        ? 'amostra genérica confirmada'
                        : 'amostras genéricas confirmadas'
                    } pelo edital`
                  : ''}
                .
              </p>
            )}
            {buscas.length > 1 && (
              <div className="curation-queries">
                <span>Buscas realizadas</span>
                {buscas.map((q) => (
                  <span key={q} className="query-chip">
                    {q}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {alerts && alerts.length > 0 && (
        <div className="alerts">
          {alerts.map((alerta) => (
            <div
              key={alerta.tipo}
              className={`alert ${alerta.tom === 'warning' ? 'alert-warning' : 'alert-info'} mb-2`}
              role="alert"
            >
              <strong>{alerta.titulo}.</strong>
              {alerta.mensagem}
            </div>
          ))}
        </div>
      )}

      {regime === 'unitario' && grupoUnidade ? (
        <div className="stat-tiles">
          <div className="stat-tile accent">
            <p className="stat-label">Preço de fechamento por {uniLabel} (mediana)</p>
            <p className="stat-value">
              {referencia.mediana !== null ? fmtBRL(referencia.mediana) : '—'}
            </p>
            <p className="stat-hint">
              {rotuloCesta ||
                `por ${uniLabel} · média ${
                  referencia.media !== null ? fmtBRL(referencia.media) : '—'
                }`}
            </p>
          </div>
          <div className="stat-tile">
            <p className="stat-label">Faixa competitiva (p25–p75) por {uniLabel}</p>
            <p className="stat-value">{faixa(referencia.p25, referencia.p75)}</p>
            <p className="stat-hint">propostas abaixo do p25 costumam vencer com folga</p>
          </div>
          <div className="stat-tile">
            <p className="stat-label">Amostras com preço por {uniLabel}</p>
            <p className="stat-value">{fmtInt(grupoUnidade.n)}</p>
            <p className="stat-hint">
              {fmtInt(grupoUnidade.derivadas || 0)} derivadas da metragem da descrição ·{' '}
              {fmtInt(stats.n_total || resultado.total)} encontradas
            </p>
          </div>
          <div className="stat-tile">
            <p className="stat-label">Contratos completos (valor global)</p>
            <p className="stat-value">
              {grupoGlobal && typeof grupoGlobal.mediana === 'number'
                ? fmtBRLCompact(grupoGlobal.mediana)
                : '—'}
            </p>
            <p className="stat-hint">
              mediana de {fmtInt(grupoGlobal ? grupoGlobal.n : 0)} contratos — escopos variados
            </p>
          </div>
        </div>
      ) : (
        <div className="stat-tiles">
          <div className="stat-tile accent">
            <p className="stat-label">
              {regime === 'global'
                ? 'Valor global típico de contrato'
                : 'Preço de fechamento (mediana)'}
            </p>
            <p className="stat-value">
              {referencia.mediana !== null ? fmtBRL(referencia.mediana) : '—'}
            </p>
            <p className="stat-hint">
              {rotuloCesta ||
                (regime === 'global'
                  ? 'mediana dos totais — escopos variados, não é preço unitário'
                  : 'valor típico pelo qual o item é homologado')}
            </p>
          </div>
          <div className="stat-tile">
            <p className="stat-label">
              {regime === 'global' ? 'Faixa de valores de contrato' : 'Faixa competitiva (p25–p75)'}
            </p>
            <p className="stat-value">{faixa(referencia.p25, referencia.p75)}</p>
            <p className="stat-hint">
              {regime === 'global'
                ? 'p25–p75 dos totais de contrato'
                : 'propostas abaixo do p25 costumam vencer com folga'}
            </p>
          </div>
          <div className="stat-tile">
            <p className="stat-label">Amostras consideradas</p>
            <p className="stat-value">
              {fmtInt(selecionados.size || filtrando ? cesta.length : referencia.n)}
            </p>
            <p className="stat-hint">
              de {fmtInt(stats.n_total || resultado.total)} encontradas
              {filtrando ? ' · filtros avançados ativos' : ''}
              {!filtrando && stats.n_outliers
                ? ` · ${fmtInt(stats.n_outliers)} outliers saneados`
                : ''}
            </p>
          </div>
          <div className="stat-tile">
            <p className="stat-label">Concorrentes · órgãos</p>
            <p className="stat-value">
              {fmtInt(referencia.fornecedores)} · {fmtInt(referencia.orgaos)}
            </p>
            <p className="stat-hint">fornecedores e compradores únicos na cesta</p>
          </div>
        </div>
      )}

      {regime === 'unitario' && grupoUnidade && grupoUnidade.escopo_similar && (
        <div className="callout success">
          <i className="material-icons">check_circle</i>
          <p>
            <strong>
              Contratações na faixa do seu projeto ({fmtInt(grupoUnidade.escopo_similar.alvo_qtd)}{' '}
              {uniLabel}):
            </strong>{' '}
            mediana de{' '}
            <strong>
              {typeof grupoUnidade.escopo_similar.mediana === 'number'
                ? `${fmtBRL(grupoUnidade.escopo_similar.mediana)}${sufixoUnidade}`
                : '—'}
            </strong>{' '}
            em {fmtInt(grupoUnidade.escopo_similar.n)} amostras com escopo entre{' '}
            {fmtInt(grupoUnidade.escopo_similar.faixa_qtd[0])} e{' '}
            {fmtInt(grupoUnidade.escopo_similar.faixa_qtd[1])} {uniLabel}.
          </p>
        </div>
      )}

      {estimativaProjeto && (
        <div className="callout project">
          <i className="material-icons">calculate</i>
          <div>
            <p className="callout-title">
              Estimativa para o seu projeto ({fmtInt(estimativaProjeto.qtd)} {uniLabel}):{' '}
              {fmtBRL(estimativaProjeto.total)}
            </p>
            <p className="callout-sub">
              {estimativaProjeto.p25 !== null && estimativaProjeto.p75 !== null
                ? `Faixa competitiva: ${fmtBRLCompact(estimativaProjeto.p25)} – ${fmtBRLCompact(
                    estimativaProjeto.p75,
                  )} · `
                : ''}
              mediana por {uniLabel} × metragem informada. Use como ordem de grandeza para a sua
              proposta — o orçamento de referência de obras segue SINAPI/SICRO.
            </p>
          </div>
        </div>
      )}

      {regime === 'global' && (
        <div className="callout warning">
          <i className="material-icons">warning_amber</i>
          <div>
            <p>
              <strong>Estes são valores TOTAIS de contratos com escopos diferentes</strong>{' '}
              (metragens e materiais distintos) — não use a mediana como preço unitário.{' '}
              {indicativo
                ? `Só ${fmtInt(
                    indicativo.stats.n,
                  )} amostras declaravam a metragem — pouco para uma referência por ${
                    indicativo.label
                  }.`
                : 'Nenhuma amostra declarava a metragem, então não foi possível normalizar por unidade física (m², km…).'}
            </p>
            {podeRefinar && (
              <button
                type="button"
                className="btn btn-outline-warning btn-sm mt-2"
                onClick={onRefinar}
                disabled={loteRodando}
              >
                Refinar a pesquisa com a IA
              </button>
            )}
          </div>
        </div>
      )}

      {indicativo && (
        <div className="callout info">
          <i className="material-icons">calculate</i>
          <p>
            <strong>
              Indicativo por {indicativo.label}: {fmtBRL(indicativo.stats.mediana || 0)}/
              {indicativo.label}
            </strong>{' '}
            (mediana de {fmtInt(indicativo.stats.n)} amostras normalizadas
            {typeof indicativo.stats.p25 === 'number' && typeof indicativo.stats.p75 === 'number'
              ? `, faixa ${fmtBRLCompact(indicativo.stats.p25)} – ${fmtBRLCompact(
                  indicativo.stats.p75,
                )}`
              : ''}
            ).
            {projetoQtd &&
            (!projetoQtd.unidade || projetoQtd.unidade === unidadeCanonica(indicativo.unidade))
              ? ` Para ${fmtInt(projetoQtd.valor)} ${indicativo.label}, isso indicaria ~${fmtBRL(
                  (indicativo.stats.mediana || 0) * projetoQtd.valor,
                )}.`
              : ''}{' '}
            Amostragem pequena: use como sinalização.
          </p>
        </div>
      )}

      {stats.n < 8 && periodoMeses > 0 && periodoMeses < 12 && (
        <div className="callout warning">
          <i className="material-icons">info</i>
          <p className="flex-grow-1">
            Poucas amostras nos últimos {periodoMeses} meses — a referência fica mais sólida com uma
            janela maior.
          </p>
          <button
            type="button"
            className="btn btn-outline-warning btn-sm"
            onClick={onAmpliarPeriodo}
            disabled={loteRodando}
          >
            Ampliar para 12 meses
          </button>
        </div>
      )}

      <div className="competitive-grid">
        <div className="competitive-card">
          <p className="comp-title">
            <i className="material-icons">emoji_events</i>
            Quem mais vence este item
          </p>
          <p className="comp-sub">
            Fornecedores com mais homologações na amostra — clique no nome para abrir a análise do
            concorrente ou no ícone para a ficha rápida.
          </p>
          {fornecedoresTop.length ? (
            <ul className={todosFornecedores ? 'expanded' : ''}>
              {fornecedoresTop.map((f, idx) => {
                const cnpjLimpo = (f.cnpj || '').toUpperCase().replace(/[^0-9A-Z]/g, '');
                return (
                  <li key={f.cnpj || f.nome}>
                    <span className="comp-rank">{idx + 1}</span>
                    <span className="comp-name">
                      {cnpjLimpo ? (
                        <Link
                          to={`/concorrente/${cnpjLimpo}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() =>
                            createTrack('Análise de Preços - Clicou em concorrente', {
                              origem: 'ranking',
                            })
                          }
                        >
                          {f.nome}
                        </Link>
                      ) : (
                        f.nome
                      )}
                    </span>
                    <span className="comp-metric">
                      <div className="metric-main">
                        {f.mediana !== null ? `${fmtBRL(f.mediana)}${sufixoUnidade}` : '—'}
                      </div>
                      <div className="metric-sub">
                        {fmtInt(f.vitorias)} homologaç{f.vitorias === 1 ? 'ão' : 'ões'}
                      </div>
                    </span>
                    {cnpjLimpo && (
                      <button
                        type="button"
                        className="comp-action d-print-none"
                        title="Ficha rápida: cadastro, sócios, itens vencidos e sanções"
                        onClick={() => abrirFicha(cnpjLimpo)}
                      >
                        <i className="material-icons">badge</i>
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="comp-empty">Sem fornecedores identificados na amostra.</p>
          )}
          {fornecedores.length > TOP && (
            <button
              type="button"
              className="btn btn-link btn-sm comp-more d-print-none"
              onClick={() => setTodosFornecedores(!todosFornecedores)}
            >
              {todosFornecedores ? 'Ver menos' : `Ver todos (${fmtInt(fornecedores.length)})`}
            </button>
          )}
        </div>

        <div className="competitive-card">
          <p className="comp-title">
            <i className="material-icons">account_balance</i>
            Quem mais compra este item
          </p>
          <p className="comp-sub">
            Órgãos com mais aquisições na amostra — clique para ver a ficha do órgão e o que ele
            pagou.
          </p>
          {orgaosTop.length ? (
            <ul className={todosOrgaos ? 'expanded' : ''}>
              {orgaosTop.map((o, idx) => (
                <li key={o.orgaoId || o.nome}>
                  <span className="comp-rank">{idx + 1}</span>
                  <button
                    type="button"
                    className="comp-name comp-link"
                    title="Ficha do órgão e aquisições nesta pesquisa"
                    onClick={() => abrirOrgao(o)}
                  >
                    {o.nome}
                    {(o.municipio || o.uf) && (
                      <span className="comp-place">
                        {' '}
                        · {[o.municipio, o.uf].filter(Boolean).join('/')}
                      </span>
                    )}
                  </button>
                  <span className="comp-metric">
                    <div className="metric-main">
                      {o.mediana !== null ? `${fmtBRL(o.mediana)}${sufixoUnidade}` : '—'}
                    </div>
                    <div className="metric-sub">
                      {fmtInt(o.compras)} aquisiç{o.compras === 1 ? 'ão' : 'ões'}
                    </div>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="comp-empty">Sem órgãos identificados na amostra.</p>
          )}
          {orgaos.length > TOP && (
            <button
              type="button"
              className="btn btn-link btn-sm comp-more d-print-none"
              onClick={() => setTodosOrgaos(!todosOrgaos)}
            >
              {todosOrgaos ? 'Ver menos' : `Ver todos (${fmtInt(orgaos.length)})`}
            </button>
          )}
        </div>
      </div>

      <div className="charts-grid">
        <ChartCard
          title="Homologações por mês"
          subtitle={`${fmtInt(hits.length)} amostras no período · por mês ou acumulado`}
        >
          <VolumeChart points={volume} />
        </ChartCard>
        <ChartCard
          title={`Distribuição de preços${rotuloUnidade}`}
          subtitle="Onde os valores homologados se concentram"
        >
          <Histogram values={valoresGrafico} />
        </ChartCard>
        <ChartCard
          title={`Tendência mensal${rotuloUnidade}`}
          subtitle={
            serie.trend_pct !== null
              ? `Mediana mês a mês · ${
                  serie.trend_pct > 0 ? '+' : ''
                }${serie.trend_pct.toLocaleString('pt-BR')}% no período`
              : 'Mediana mês a mês com faixa p25–p75'
          }
        >
          <TrendChart points={serie.points} />
        </ChartCard>
        <ChartCard
          title={`Mediana por região${rotuloUnidade}`}
          subtitle="Onde o item fecha mais caro ou mais barato"
        >
          <RegionChart hits={hits} valorDe={valorComparavel} regiaoDestaque={regiaoDestaque} />
        </ChartCard>
      </div>

      <div className="samples-card">
        <div className="samples-head">
          <div>
            <p className="samples-title">
              Amostras ({fmtInt(hits.length)}
              {filtrando ? ` de ${fmtInt(todasAmostras.length)}` : ''})
            </p>
            <p className="samples-sub">
              {visao === 'amostras'
                ? 'Selecione amostras para refinar a referência — sem seleção, todas entram na cesta.'
                : 'Amostras agrupadas por licitação — clique para ver o resultado completo.'}
            </p>
          </div>
          <div className="samples-tools">
            <div className="view-toggle d-print-none" role="group" aria-label="Visão das amostras">
              <button
                type="button"
                className={visao === 'amostras' ? 'active' : ''}
                onClick={() => mudarVisao('amostras')}
              >
                Itens
              </button>
              <button
                type="button"
                className={visao === 'licitacoes' ? 'active' : ''}
                onClick={() => mudarVisao('licitacoes')}
              >
                Licitações ({fmtInt(licitacoes.length)})
              </button>
            </div>
            {visao === 'amostras' && selecionados.size > 0 && (
              <button
                type="button"
                className="btn btn-link btn-sm"
                onClick={() => setSelecionados(new Set())}
              >
                Limpar seleção ({fmtInt(selecionados.size)})
              </button>
            )}
            <select
              className="custom-select"
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value as SortMode)}
              aria-label="Ordenar amostras"
            >
              <option value="relevancia">Mais relevantes</option>
              <option value="valor_asc">Menor valor</option>
              <option value="valor_desc">Maior valor</option>
              <option value="data_desc">Mais recentes</option>
              <option value="data_asc">Mais antigas</option>
            </select>
          </div>
        </div>

        {visao === 'amostras' ? (
          <PriceIntelligenceSampleTable
            hits={hitsVisiveis}
            regime={regime}
            selecionados={selecionados}
            onToggle={toggle}
            onToggleTodos={toggleTodos}
            todosSelecionados={todosVisiveisSelecionados}
            ordenacao={sortMode}
            onOrdenar={ordenarPor}
          />
        ) : (
          <PriceIntelligenceBiddingTable grupos={licitacoes} />
        )}

        {visao === 'amostras' && hitsOrdenados.length > visiveis && (
          <div className="samples-footer">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setVisiveis((v) => v + 25)}
            >
              Mostrar mais {fmtInt(Math.min(25, hitsOrdenados.length - visiveis))} de{' '}
              {fmtInt(hitsOrdenados.length - visiveis)} restantes
            </button>
          </div>
        )}
      </div>

      <div className="basket-bar">
        <span className="basket-count">
          <span className="count-pill">{fmtInt(cesta.length)}</span>
          {selecionados.size
            ? 'amostras selecionadas'
            : 'amostras na cesta (selecione para refinar)'}
        </span>
        <span className="basket-stats">
          <span>
            mediana{' '}
            <strong>
              {referencia.mediana !== null ? `${fmtBRL(referencia.mediana)}${sufixoUnidade}` : '—'}
            </strong>
          </span>
          <span>
            média <strong>{referencia.media !== null ? fmtBRL(referencia.media) : '—'}</strong>
          </span>
          <span>
            menor <strong>{referencia.menor !== null ? fmtBRL(referencia.menor) : '—'}</strong>
          </span>
        </span>
        <span className="basket-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={baixarCsv}>
            <i className="material-icons">download</i>
            CSV
          </button>
          <button type="button" className="btn btn-primary btn-ia btn-sm" onClick={salvar}>
            <i className="material-icons">bookmark_border</i>
            Salvar pesquisa
          </button>
        </span>
      </div>

      <PriceIntelligenceOrgaoModal
        orgao={orgaoAberto}
        hits={hits}
        valorDe={valorComparavel}
        sufixoUnidade={sufixoUnidade}
        onClose={() => setOrgaoAberto(null)}
      />
      <ProvidersModal
        showModal={fichaAberta}
        setShowModal={setFichaAberta}
        setCurrentPageBidding={setPaginaFicha}
        currentPageBidding={paginaFicha}
      />
    </Container>
  );
};

export default PriceIntelligenceResults;
